export type SuccessStatus = 'yes' | 'no' | 'ignored' | '';
export type SuccessPeriod = 'all' | '24h' | '7d' | '30d' | '90d';
export const SUCCESS_PERIODS: { value: SuccessPeriod; label: string }[] = [
  { value: 'all', label: 'Alle' },
  { value: '24h', label: 'Letzte 24 Stunden' },
  { value: '7d', label: 'Letzte 7 Tage' },
  { value: '30d', label: 'Letzte 30 Tage' },
  { value: '90d', label: 'Letzte 90 Tage' },
];
export type SuccessStats = {
  yes: number;
  no: number;
  ignored: number;
  unreviewed: number;
  total: number;
  rate: number | null;
};
type Review = {
  value: SuccessStatus;
  loaded: boolean;
  saving: boolean;
  error: string | null;
  saved: boolean;
  version: number;
  reading: boolean;
};
type SuccessAPI = {
  reviews: (ids: string[]) => Promise<string>;
  save: (id: string, status: SuccessStatus) => Promise<string>;
  stats: (search: string, period: SuccessPeriod) => Promise<string>;
};
const isStatus = (value: unknown): value is SuccessStatus =>
  value === '' || value === 'yes' || value === 'no' || value === 'ignored';
const emptyReview = (): Review => ({
  value: '',
  loaded: false,
  saving: false,
  error: null,
  saved: false,
  version: 0,
  reading: false,
});
const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';
const bounded = <T>(work: Promise<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('Zeitüberschreitung')), 60000);
    work.then(resolve, reject).finally(() => clearTimeout(timer));
  });

// Stores in the same tab invalidate each other after an acknowledged write, never from an optimistic value.
const reviewChanges = new Set<(id: string, source: CallSuccessStore) => void>();

/** One store per view: batched row reads, confirmed writes and optional scope-bound aggregate reads. */
export class CallSuccessStore {
  private rows = new Map<string, Review>();
  private listeners = new Set<() => void>();
  private rowListeners = new Map<string, Set<() => void>>();
  private waiting = new Set<string>();
  private scheduled: ReturnType<typeof setTimeout> | undefined;
  private timer: ReturnType<typeof setInterval> | undefined;
  private disposed = false;
  private channel: BroadcastChannel | undefined;
  private stopLiveUpdates: (() => void) | undefined;
  private statsVersion = 0;
  private statsRunning = false;
  private statsQueued = false;
  private search = '';
  private period: SuccessPeriod = 'all';
  stats: SuccessStats | null = null;
  statsLoading = true;
  statsError: string | null = null;
  constructor(
    private api: SuccessAPI,
    private options: { includeStats?: boolean } = {},
  ) {}
  row(id: string): Readonly<Review> {
    if (!this.rows.has(id)) this.rows.set(id, emptyReview());
    return this.rows.get(id)!;
  }
  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  subscribeRow(id: string, listener: () => void) {
    if (!this.rowListeners.has(id)) this.rowListeners.set(id, new Set());
    this.rowListeners.get(id)!.add(listener);
    this.row(id);
    this.requestRow(id);
    return () => {
      const listeners = this.rowListeners.get(id);
      listeners?.delete(listener);
      if (!listeners?.size) this.rowListeners.delete(id);
    };
  }
  private notify(id?: string) {
    if (this.disposed) return;
    for (const listener of id === undefined ? this.listeners : (this.rowListeners.get(id) ?? [])) listener();
  }
  start() {
    if (this.disposed) return () => {};
    if (!this.stopLiveUpdates) {
      const reloadVisible = () => {
        if (visible()) this.refresh();
      };
      const changed = (id: string, source?: CallSuccessStore) => {
        if (source === this || this.disposed) return;
        if (this.rowListeners.has(id)) this.requestRow(id);
        this.refreshStats();
      };
      reviewChanges.add(changed);
      try {
        if (typeof BroadcastChannel !== 'undefined') {
          this.channel = new BroadcastChannel('zw-call-success-review');
          this.channel.onmessage = event => {
            const id = event.data?.callID;
            if (typeof id === 'string' && id.length > 0 && id.length <= 256) changed(id);
          };
        }
      } catch {
        // Focus and bounded polling still synchronize browsers where BroadcastChannel is unavailable.
      }
      if (typeof window !== 'undefined') window.addEventListener('focus', reloadVisible);
      if (typeof document !== 'undefined') document.addEventListener('visibilitychange', reloadVisible);
      this.stopLiveUpdates = () => {
        reviewChanges.delete(changed);
        this.channel?.close();
        this.channel = undefined;
        if (typeof window !== 'undefined') window.removeEventListener('focus', reloadVisible);
        if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', reloadVisible);
        this.stopLiveUpdates = undefined;
      };
    }
    if (!this.timer)
      this.timer = setInterval(() => {
        if (visible()) this.refresh(false);
      }, 15000);
    return () => {
      if (this.timer) clearInterval(this.timer);
      this.timer = undefined;
      this.stopLiveUpdates?.();
    };
  }
  matchesScope(search: string, period: SuccessPeriod) {
    return this.search === search && this.period === period;
  }
  configure(search: string, period: SuccessPeriod) {
    if (search !== this.search || period !== this.period) {
      this.search = search;
      this.period = period;
      this.stats = null;
    }
    this.refresh();
  }
  refresh(invalidateStats = true) {
    if (this.disposed) return;
    for (const id of this.rowListeners.keys()) this.requestRow(id);
    this.refreshStats(invalidateStats);
  }
  private requestRow(id: string) {
    if (!id || this.disposed) return;
    this.waiting.add(id);
    this.schedule();
  }
  private schedule() {
    if (this.scheduled !== undefined || this.disposed) return;
    this.scheduled = setTimeout(() => {
      this.scheduled = undefined;
      const ids = [...this.waiting].filter(id => {
        const row = this.rows.get(id);
        return row && !row.saving && !row.reading;
      });
      for (let i = 0; i < ids.length; i += 100) void this.readRows(ids.slice(i, i + 100));
    }, 0);
  }
  private async readRows(ids: string[]) {
    const versions = ids.map(id => {
      this.waiting.delete(id);
      const row = this.rows.get(id)!;
      row.reading = true;
      return row.version;
    });
    try {
      const values = JSON.parse(await bounded(this.api.reviews(ids)));
      if (!values || typeof values !== 'object' || Array.isArray(values)) throw new Error('Invalid reviews');
      ids.forEach((id, i) => {
        const row = this.rows.get(id)!;
        if (row.version !== versions[i] || this.disposed) return;
        const value = values[id] ?? '';
        if (!isStatus(value)) throw new Error('Invalid review');
        row.value = value;
        row.loaded = true;
        row.error = null;
        this.notify(id);
      });
    } catch {
      ids.forEach((id, i) => {
        const row = this.rows.get(id)!;
        if (row.version === versions[i]) {
          row.error = 'Bewertung konnte nicht geladen werden.';
          this.notify(id);
        }
      });
    } finally {
      for (const id of ids) this.rows.get(id)!.reading = false;
      if (this.waiting.size) this.schedule();
    }
  }
  async save(id: string, value: SuccessStatus): Promise<boolean> {
    const row = this.rows.get(id);
    if (!row?.loaded || row.saving || !isStatus(value) || this.disposed) return false;
    if (value === row.value) return true;
    row.saving = true;
    row.saved = false;
    row.error = null;
    row.version++;
    this.notify(id);
    try {
      const saved = JSON.parse(await bounded(this.api.save(id, value)));
      if (!saved || saved.callID !== id || saved.status !== value) throw new Error('Save was not confirmed');
      if (this.disposed) return false;
      row.value = value;
      row.saved = true;
      this.refreshStats();
      for (const changed of reviewChanges) changed(id, this);
      // Announce only the ID; other tabs re-read their authorized backend data.
      try {
        this.channel?.postMessage({ callID: id });
      } catch {
        // A suspended/closing tab will recover through its focus refresh or polling.
      }
      return true;
    } catch {
      row.error = 'Speichern fehlgeschlagen. Die bisherige Bewertung bleibt bestehen.';
      return false;
    } finally {
      row.saving = false;
      this.notify(id);
      if (this.waiting.size) this.schedule();
    }
  }
  private refreshStats(invalidate = true) {
    if (this.disposed || this.options.includeStats === false) return;
    // Routine polling must let a slow same-scope read finish. Saves/filter changes still invalidate it.
    if (this.statsRunning && !invalidate) {
      this.statsQueued = true;
      return;
    }
    this.statsVersion++;
    this.statsLoading = true;
    this.statsError = null;
    this.statsQueued = true;
    this.notify();
    void this.readStats();
  }
  private async readStats() {
    if (this.statsRunning || this.disposed || !this.statsQueued) return;
    this.statsQueued = false;
    this.statsRunning = true;
    const version = this.statsVersion;
    try {
      const value = JSON.parse(await bounded(this.api.stats(this.search, this.period)));
      if (
        !value ||
        !['yes', 'no', 'ignored', 'unreviewed', 'total'].every(
          key => Number.isSafeInteger(value[key]) && value[key] >= 0,
        ) ||
        value.yes + value.no + value.ignored + value.unreviewed !== value.total
      )
        throw new Error('Invalid statistics');
      if (version === this.statsVersion && !this.disposed) {
        this.stats = value;
        this.statsLoading = false;
        this.statsError = null;
      }
    } catch {
      if (version === this.statsVersion && !this.disposed) {
        this.statsLoading = false;
        this.statsError = 'Auswertung konnte nicht aktualisiert werden.';
      }
    } finally {
      this.statsRunning = false;
      this.notify();
      if (this.statsQueued) void this.readStats();
    }
  }
  dispose() {
    this.disposed = true;
    this.stopLiveUpdates?.();
    if (this.timer) clearInterval(this.timer);
    if (this.scheduled !== undefined) clearTimeout(this.scheduled);
    this.listeners.clear();
    this.rowListeners.clear();
    this.waiting.clear();
  }
}
